import { functions, auth } from "./initializer";
import version from "version";
import compareVersions from "compare-versions";
import { getIdToken } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import {
  Snapshot,
  UpdateSnapshot,
} from "stores/modules/entities.room.savedatas";

const diceroll = httpsCallable<
  { command: string; system: string },
  RollResult | null
>(functions, "diceroll");
const systeminfo = httpsCallable<
  { system: string },
  { systeminfo: { info: string } }
>(functions, "systeminfo");
const commandPattern = httpsCallable<{ system: string }, string>(
  functions,
  "commandPattern"
);
const dicebotVersion = httpsCallable<void, string>(functions, "dicebotVersion");
const names = httpsCallable<
  void,
  { names: { name: string; system: string; sortKey: string }[] }
>(functions, "names");
const getDeployedVersion = httpsCallable<void, string>(functions, "version");
const _getTermsUpdatedAt = httpsCallable<void, string>(
  functions,
  "termsUpdatedAt"
);
const recursiveDeleteRoomData = httpsCallable<{
  path: string;
  roomId: string;
  options?: OptionsDeleteAllRoomMessages;
}>(functions, "recursiveDeleteRoomData");
const duplicateRoom = httpsCallable<{
  roomId: string;
  subcollections?: string[];
}>(functions, "duplicateRoom");
const getFileMetadataCall = httpsCallable<string, any>(
  functions,
  "getFileMetadata"
);
const deleteFileCall = httpsCallable<string, boolean>(functions, "deleteFile");
const deleteAccountCall = httpsCallable<void, boolean>(
  functions,
  "deleteAccount"
);
const _getKanbanAds = httpsCallable<void, KanbanAd[]>(
  functions,
  "getKanbanAds"
);
const _getProducts = httpsCallable<void, Product[]>(functions, "getProducts");
const _getProductById = httpsCallable<{ productId: string }, Product>(
  functions,
  "getProductById"
);
const _getProductsLatestPublishedAt = httpsCallable<void, string>(
  functions,
  "getProductsLatestPublishedAt"
);
const _getFilteredProductsCategory = httpsCallable<
  {
    category: string[];
    productType: "game" | "extention";
    cursor: string | null;
    limit: number;
  },
  GetFilteredProductResponse
>(functions, "getFilteredProductsCategory");
const _getCategoryList = httpsCallable<void, { id: number; name: string }[]>(
  functions,
  "getCategoryList"
);
const _getGamesProduct = httpsCallable<{ productId: string }, Product>(
  functions,
  "getGamesProduct"
);
const _getGamesProductsInLibrary = httpsCallable<void, Product[]>(
  functions,
  "getGamesProductsInLibrary"
);
const _getGamesPurchase = httpsCallable<
  { productId: string },
  { purchased: boolean }
>(functions, "getGamesPurchase");
const _createProductCheckoutUrl = httpsCallable<
  { productId: string },
  { url: string }
>(functions, "createProductCheckoutUrl");
const _createFreeProductPurchase = httpsCallable<
  { productId: string },
  { purchased: boolean }
>(functions, "createFreeProductPurchase");
const _syncPurchaseState = httpsCallable<
  { productId: string },
  { purchased: boolean }
>(functions, "syncPurchaseState");
const _getGamesPurchasedProductIds = httpsCallable<void, string[]>(
  functions,
  "getGamesPurchasedProductIds"
);
const _getDownloadAttachmentUrl = httpsCallable<
  { productId: string; filename: string },
  { url: string }
>(functions, "getDownloadAttachmentUrl");
const _getSubscriptionStatus = httpsCallable<void, SubscriptionStatus>(
  functions,
  "getSubscriptionStatus"
);
const _createSubscriptionSession = httpsCallable<
  { plan: string },
  { url: string }
>(functions, "createSubscriptionSession");
const _createBillingPortalSession = httpsCallable<void, { url: string }>(
  functions,
  "createBillingPortalSession"
);
const _joinSubscription = httpsCallable<
  { plans: ("pro" | "sound-master")[] },
  { url: string }
>(functions, "joinSubscription");
const _previewJoinSoundMaster = httpsCallable<
  void,
  ResultPreviewJoinSoundMaster
>(functions, "previewJoinSoundMaster");
const _cancelSubscription = httpsCallable<
  void,
  { status: "success" | "already-cancelled"; cancelAt: string }
>(functions, "cancelSubscription");
const _getPaymentCount = httpsCallable<void, number>(
  functions,
  "getPaymentCount"
);
const _getOfficialPlaylists = httpsCallable<void, OfficialPlaylist[]>(
  functions,
  "getOfficialPlaylists"
);
const _getOfficialImages = httpsCallable<void, OfficialImageDir[]>(
  functions,
  "getOfficialImages"
);
const _createSnapshot = httpsCallable<
  { roomId: string },
  { snapshotId: string }
>(functions, "createSnapshot");
const _importSnapshot = httpsCallable<{
  roomId: string;
  snapshotId: string;
  snapshot: Snapshot;
}>(functions, "importSnapshot");
const _getSnapshot = httpsCallable<
  { roomId: string; snapshotId: string },
  UpdateSnapshot
>(functions, "getSnapshot");
const _deleteSnapshot = httpsCallable<{ roomId: string; snapshotId: string }>(
  functions,
  "deleteSnapshot"
);

const _syncSubscriptionState = httpsCallable<
  { force: boolean },
  "free" | "ccfolia-pro"
>(functions, "syncSubscriptionState");
const _publishRoomPackage = httpsCallable<
  { roomId: string },
  { roomPackageId: string }
>(functions, "publishRoomPackage");
const _getRoomPackage = httpsCallable<unknown, RoomPackageInfo>(
  functions,
  "getRoomPackage"
);
const _updateRoomPackage = httpsCallable<
  { roomPackageId: string; updatePackage: Partial<RoomPackageInfo> },
  void
>(functions, "updateRoomPackage");
const _deleteRoomPackage = httpsCallable<{ roomPackageId: string }, void>(
  functions,
  "deleteRoomPackage"
);
const _createRoomFromRoomPackage = httpsCallable<
  { roomPackageId: string },
  { roomId: string }
>(functions, "createRoomFromRoomPackage");
const _applyRoomPackage = httpsCallable<
  { roomPackageId: string; roomId: string; applyOptions: ApplyOptions },
  void
>(functions, "applyRoomPackage");
const _getPartnersService = httpsCallable<void, ServiceInfo>(
  functions,
  "getPartnersService"
);
const _getServiceCampaign = httpsCallable<
  { serviceId: string },
  ServiceCampaign
>(functions, "getCampaignsByService");
const _getCampaignsLatestPublishedAt = httpsCallable<void, string>(
  functions,
  "getCampaignsLatestPublishedAt"
);
const _sendSurveyAnswerInfo = httpsCallable<SurveyAnsewerOptions, void>(
  functions,
  "sendSurveyAnswerInfo"
);
const _checkedSurvey = httpsCallable<{ surveyId: string }, void>(
  functions,
  "checkedSurvey"
);
const _getInformations = httpsCallable<{ surveyId: string | null }, AdContent>(
  functions,
  "getInformations"
);
const _rollTable = httpsCallable<{ command: string }, RollResult | null>(
  functions,
  "rollTable"
);

// sound master

type ResultGetSoundMasterPrice = {
  price: number;
  campaignPrice?: number;
  campaignLabel?: string;
};

const _getSoundMasterPrice = httpsCallable<void, ResultGetSoundMasterPrice>(
  functions,
  "getSoundMasterPrice"
);

type ResultJoinSoundMaster =
  | { status: "active" }
  | { status: "failure" }
  | { status: "expired_preview" }
  | { status: "requires_action"; clientSecret: string };

const _joinSoundMaster = httpsCallable<
  { amount: number; prorationDate?: number },
  ResultJoinSoundMaster
>(functions, "joinSoundMaster");

const _cancelSoundMaster = httpsCallable<void, { status: "success" }>(
  functions,
  "cancelSoundMaster"
);

const _reactivateSoundMaster = httpsCallable<void, { status: "success" }>(
  functions,
  "reactivateSoundMaster"
);

// functions dev
// functions.useFunctionsEmulator("http://localhost:5000");

const CF_ENDPOINT = process.env.REACT_APP_CF_ENDPOINT;
// const CF_ENDPOINT = "http://localhost:5000/ccfolia-160aa/asia-northeast1";

export const checkClientVersion = async () => {
  if (process.env.NODE_ENV !== "production") {
    return null;
  }
  try {
    const res = await getDeployedVersion();
    // const res = { data: "1.6.1" };
    const compared = compareVersions(res.data, version);
    return compared > 0 ? res.data : null;
  } catch (_) {
    return null;
  }
};

type OptionsDeleteAllRoomMessages = {
  eventTrigger?: string;
};

export const deleteAllRoomMessages = async (
  roomId: string,
  options?: OptionsDeleteAllRoomMessages
) => {
  try {
    return recursiveDeleteRoomData({
      roomId,
      path: "messages",
      options: {
        ...options,
        eventTrigger: options?.eventTrigger || "call-api-deleteAllRoomMessages",
      },
    });
  } catch (err) {
    return err;
  }
};

export const getDiceBotVersion = async () => {
  try {
    const res = await dicebotVersion();
    if (res.data) {
      return res.data + "";
    }
  } catch (err) {}
  return "";
};

export const getDiceBotSystems = async () => {
  try {
    const res = await names();
    if (res.data) {
      return res.data;
    }
  } catch (err) {}
};

export const getDiceBotSystemsInfo = async (system: string | null) => {
  try {
    if (system == null) {
      return;
    }

    const res = await systeminfo({ system });
    if (res.data) {
      return res.data;
    }
  } catch (err) {}
};

const commandPatternCache: { [system: string]: RegExp } = {};
export const getDiceBotCommandPattern = async (system: string) => {
  const pattern = commandPatternCache[system];
  if (pattern) {
    return pattern;
  }

  try {
    const res = await commandPattern({ system });
    if (typeof res.data === "string") {
      const regexp = new RegExp(res.data, "i");
      commandPatternCache[system] = regexp;
      return regexp;
    }
  } catch (err) {}
};

type RollResult = {
  result: string;
  secret: boolean;
  success: boolean;
  failure: boolean;
  critical: boolean;
  fumble: boolean;
  dices: { kind: string; faces: number; value: number }[];
};

const convertHalfWidthText = (text: string) => {
  return text
    .replace(/[！-～]/g, (str) => {
      return String.fromCharCode(str.charCodeAt(0) - 0xfee0);
    })
    .replace(/”/g, '"')
    .replace(/’/g, "'")
    .replace(/‘/g, "`")
    .replace(/￥/g, "\\")
    .replace(/　/g, " ")
    .replace(/〜/g, "~");
};
export const sendDiceBotCommand = async (
  system: string | null,
  command: string
) => {
  try {
    command = convertHalfWidthText(command);
    const commandPattern = await getDiceBotCommandPattern(system || "");
    if (commandPattern == null || !commandPattern.test(command)) {
      return null;
    }
    const res = await diceroll({ system: system || "", command });
    if (res.data) {
      return res.data;
    }
  } catch (err) {}
};

export const rollTable = async (command: string) => {
  try {
    command = convertHalfWidthText(command);
    const res = await _rollTable({ command });
    if (res.data) {
      return res.data;
    }
  } catch (err) {
    return null;
  }
};

export const duplicateRoomFunc = async ({
  roomId,
  subcollections,
}: {
  roomId: string;
  subcollections: string[];
}) => {
  try {
    const res = await duplicateRoom({ roomId, subcollections });
    window.location.href = "/home"; // temporary fix
    return res;
  } catch (err) {
    return err;
  }
};

export const uploadFile = async ({ file, filePath }) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("filePath", filePath);
  try {
    const idToken = auth.currentUser
      ? await getIdToken(auth.currentUser)
      : undefined;
    const res = await fetch(`${CF_ENDPOINT}/uploadFile`, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    return res.json();
  } catch (err) {
    console.error(err);
    return null;
  }
};

export const uploadFileV2 = async ({ file, filePath }) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("filePath", filePath);
  try {
    const idToken = auth.currentUser
      ? await getIdToken(auth.currentUser)
      : undefined;
    const res = await fetch(`${CF_ENDPOINT}/uploadFileV2`, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    return res.json();
  } catch (err) {
    console.error(err);
    return null;
  }
};

export const getFileMetadata = async (filePath: string) => {
  try {
    const res = await getFileMetadataCall(filePath);
    if (res.data) {
      return res.data;
    }
  } catch (err) {
    console.error(err);
  }
};

export const deleteFile = async (filePath: string) => {
  try {
    const res = await deleteFileCall(filePath);
    if (res.data) {
      return res.data;
    }
  } catch (err) {
    console.error(err);
  }
};

export const deleteAccount = async () => {
  try {
    const res = await deleteAccountCall();
    if (res.data) {
      window.location.reload();
      return true;
    }
  } catch (err) {
    console.error(err);
  }
  return false;
};

export type AdContent =
  | {
      type: "ad";
      content: KanbanAd[];
    }
  | {
      type: "survey";
      content: Survey;
    };

export type KanbanAd = {
  kind: "ad" | "games";
  campaignId: string;
  name: string;
  imageUrl: string;
  videoUrl: string;
  muted: boolean;
  url: string;
};

export type Survey = {
  id: string;
  client: string;
  startMessage: string | null;
  priority: number;
  questions: {
    id: string;
    text: string;
    type: "once" | "multi" | "free";
    choice?: {
      id: string;
      text: string;
    }[];
  }[];
};

export const getAdDialogContents = async () => {};

export const getKanbanAds = async (): Promise<KanbanAd[]> => {
  const res = await _getKanbanAds();
  return res.data;
};

export const getProductsLatestPublishedAt = async (): Promise<string> => {
  const res = await _getProductsLatestPublishedAt();
  return res.data;
};

export type Product = {
  id: string;
  name: string;
  shortName: string;
  kind: "game" | "extention";
  category: string;
  players: string;
  playTimes: string;
  developer: string;
  officialSite?: {
    name: string;
    url: string;
  };
  releasedAt: string;
  publishedAt: string; // ISO Date String
  notAvailable: boolean;
  price: number;
  contents: string;
  description: string;
  details: string;
  thumbnailUrl: string;
  carouselItems: Visual[];
  roomPackageId?: string;
  applyOptions?: ApplyOptions;
  externalShopUrl?: string;
  parentProduct?: Product;
  relatedProducts: Product[]; // 追加・拡張(公開済商品)
  candidateRelatedProducts: Product[]; // 追加・拡張(購入済候補商品)
  relatedItems: Product[]; // 関連商品
  attachments: ProductAttachment[];
  isOther: boolean;
  storeCarouselThumbnailUrl: string;
};

export type GetFilteredProductResponse = {
  data: Product[];
  hasMore: boolean;
  nextCursor: string | null;
};

type ProductAttachment = {
  filename: string;
  size: string;
  mime: string;
  updatedAt: string; // ISO Date String
};

export type Visual = VisualImage | VisualVideo;

export type VisualImage = {
  kind: "image";
  url: string;
};

export type VisualVideo = {
  kind: "video";
  url: string;
  thumbnailUrl: string;
};

export const getProducts = async (): Promise<Product[]> => {
  const res = await _getProducts();
  return res.data;
};

export const getProductById = async (productId: string): Promise<Product> => {
  const res = await _getProductById({ productId });
  return res.data;
};

export const getFilteredProductsCategory = async (
  category: string[],
  productType: "game" | "extention",
  cursor: string | null,
  limit: number
): Promise<GetFilteredProductResponse> => {
  const res = await _getFilteredProductsCategory({
    category,
    productType,
    cursor,
    limit,
  });
  return res.data;
};

export const getCategoryList = async (): Promise<
  { id: number; name: string }[]
> => {
  const res = await _getCategoryList();
  return res.data;
};

export const getGamesProduct = async (
  productId: string
): Promise<Product | null> => {
  try {
    const res = await _getGamesProduct({ productId });
    if (res.data) {
      return res.data;
    }
  } catch (err) {
    return null;
  }
  return null;
};

export const getGamesProductsInLibrary = async (): Promise<Product[]> => {
  const res = await _getGamesProductsInLibrary();
  return res.data;
};

export const getGamesPurchase = async (
  productId: string
): Promise<{ purchased: boolean }> => {
  const res = await _getGamesPurchase({ productId });
  return res.data;
};

export const createProductCheckoutUrl = async (
  productId: string
): Promise<{ url: string }> => {
  const res = await _createProductCheckoutUrl({ productId });
  return res.data;
};

export const getDownloadAttachmentUrl = async (
  productId: string,
  filename: string
): Promise<{ url: string }> => {
  const res = await _getDownloadAttachmentUrl({ productId, filename });
  return res.data;
};

export const createFreeProductPurchase = async (
  productId: string
): Promise<{ purchased: boolean }> => {
  const res = await _createFreeProductPurchase({ productId });
  return res.data;
};

export const syncPurchaseState = async (
  productId: string
): Promise<{ purchased: boolean }> => {
  const res = await _syncPurchaseState({ productId });
  return res.data;
};

export const getGamesPurchasedProductIds = async () => {
  const res = await _getGamesPurchasedProductIds();
  return res.data;
};

export const getTermsUpdatedAt = async () => {
  try {
    const res = await _getTermsUpdatedAt();
    if (res.data) {
      return res.data;
    }
  } catch (err) {
    return null;
  }
  return null;
};

type SubscriptionStatus = {
  pro: SubscriptionStatusItem;
  soundMaster: SubscriptionStatusItem;
};

type SubscriptionStatusItem =
  | {
      status: "none";
    }
  | {
      status: "active";
      currentPeriodEndAt: string;
      cancelAt?: string;
    };

export const getSubscriptionStatus = async (): Promise<SubscriptionStatus> => {
  try {
    const res = await _getSubscriptionStatus();
    return res.data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export const createSubscription = async (): Promise<{ url: string }> => {
  try {
    const res = await _createSubscriptionSession({ plan: "ccfolia-pro" });
    return res.data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export const getSoundMasterPrice = async () => {
  const res = await _getSoundMasterPrice();
  return res.data;
};

type ResultPreviewJoinSoundMaster = {
  amount: number;
  prorationDate: number; // unix timestamp
};

export const previewJoinSoundMaster =
  async (): Promise<ResultPreviewJoinSoundMaster> => {
    try {
      const res = await _previewJoinSoundMaster();
      return res.data;
    } catch (err) {
      console.log(err);
      return Promise.reject(err);
    }
  };

export const joinSubscription = async (
  plans: ("pro" | "sound-master")[]
): Promise<{ url: string }> => {
  try {
    const res = await _joinSubscription({ plans });
    return res.data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export const cancelSubscription = async (): Promise<{
  status: "success" | "already-cancelled";
  cancelAt: string;
}> => {
  try {
    const res = await _cancelSubscription();
    return res.data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export const createBillingPortalSession = async (): Promise<{
  url: string;
}> => {
  try {
    const res = await _createBillingPortalSession();
    return res.data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export const getPaymentCount = async (): Promise<number> => {
  const res = await _getPaymentCount();
  return res.data;
};

export const joinSoundMaster = async (
  expectedAmount: number,
  prorationDate: number | undefined
): Promise<ResultJoinSoundMaster> => {
  try {
    const res = await _joinSoundMaster({
      amount: expectedAmount,
      prorationDate,
    });
    return res.data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export const cancelSoundMaster = async (): Promise<void> => {
  await _cancelSoundMaster();
};

export const reactivateSoundMaster = async (): Promise<void> => {
  await _reactivateSoundMaster();
};

export type OfficialPlaylist = {
  id: string;
  name: string;
  priority: number;
  free: boolean;
  sounds: PlaylistSound[];
};

export type PlaylistSound = {
  id: string;
  name: string;
  provider: string;
  volume: number;
  repeat: boolean;
  url: string;
  addedAt?: string; // ISO Date String
};

export const getOfficialPlaylists = async (): Promise<OfficialPlaylist[]> => {
  try {
    const res = await _getOfficialPlaylists();
    return res.data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export const syncSubscriptionState = async (options: {
  force: boolean;
}): Promise<"free" | "ccfolia-pro"> => {
  try {
    const res = await _syncSubscriptionState(options);
    return res.data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export const publishRoomPackage = async (
  roomId: string
): Promise<{ roomPackageId: string }> => {
  try {
    const res = await _publishRoomPackage({ roomId });
    return res.data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export type RoomPackageInfo = {
  ownerDigest: string;
  ownerName: string;
  name: string;
  thumbnailUrl?: string | null;
  productId: string | null;
  purchased: boolean;
  allowDuplicate: boolean;
  createdAt: string;
  updatedAt: string;
};

export const getRoomPackage = async (
  roomPackageId: string
): Promise<RoomPackageInfo> => {
  try {
    const res = await _getRoomPackage({ roomPackageId });
    return res.data;
  } catch (err) {
    console.error(err);
    return Promise.reject(err);
  }
};

export const updateRoomPackage = async (
  roomPackageId: string,
  updatePackage: Partial<RoomPackageInfo>
): Promise<void> => {
  try {
    await _updateRoomPackage({ roomPackageId, updatePackage });
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export const deleteRoomPackage = async (
  roomPackageId: string
): Promise<void> => {
  try {
    await _deleteRoomPackage({ roomPackageId });
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export const createRoomFromRoomPackage = async (
  roomPackageId: string
): Promise<{ roomId: string }> => {
  try {
    const res = await _createRoomFromRoomPackage({ roomPackageId });
    return res.data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export type ApplyOptions = {
  room: boolean;
  characters: boolean;
  decks: boolean;
  effects: boolean;
  items: boolean;
  notes: boolean;
  scenes: boolean;
  savedatas: boolean;
};

export const applyRoomPackage = async (
  roomId: string,
  roomPackageId: string,
  applyOptions: ApplyOptions
) => {
  await _applyRoomPackage({ roomId, roomPackageId, applyOptions });
  return;
};

export const createSnapshot = async (
  roomId: string
): Promise<{ snapshotId: string }> => {
  try {
    const res = await _createSnapshot({ roomId });
    return res.data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export const importSnapshot = async (
  roomId: string,
  snapshotId: string,
  snapshot: Snapshot
): Promise<unknown> => {
  try {
    const res = await _importSnapshot({ roomId, snapshotId, snapshot });
    return res.data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export const getSnapshot = async (
  roomId: string,
  snapshotId: string
): Promise<UpdateSnapshot> => {
  try {
    const res = await _getSnapshot({ roomId, snapshotId });
    return res.data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export const deleteSnapshot = async (
  roomId: string,
  snapshotId: string
): Promise<void> => {
  try {
    await _deleteSnapshot({ roomId, snapshotId });
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export type OfficialImageDir = {
  id: string;
  name: string;
  sortKey: number;
  images: OfficialImage[];
};

export type OfficialImage = {
  id: string;
  name: string;
  url: string;
};

export const getOfficialImageDirs = async (): Promise<OfficialImageDir[]> => {
  try {
    const res = await _getOfficialImages();
    const data = res.data;
    return data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export type PartnerService = {
  id: string;
  name: string;
  headerName: string;
  detail: string;
  imageUrl: string;
  headerImageUrl: string;
  officialSiteUrl: string;
  order: number;
  tags: string[];
};

export type PartnerCampaign = {
  id: string;
  serviceId: string;
  campaignId: string;
  title: string;
  detail: string;
  summary: string;
  imageUrl: string;
  specialOfferImageUrl: string;
  topImageUrl: string;
  termTitle: string;
  campaignTerm: string;
  publishedAt: string;
  isSpecialOffer: boolean;
  isTop: boolean;
  order: number;
};

export type ServiceInfo = {
  services: PartnerService[];
  topCampaigns: PartnerCampaign[];
  specialCampaign?: PartnerCampaign;
};

export type ServiceCampaign = {
  error?: string;
  service: PartnerService;
  campaigns: PartnerCampaign[];
};

export const getPartnersService = async (): Promise<ServiceInfo> => {
  const res = await _getPartnersService();
  return res.data;
};

export const getPartnersCampaignByService = async (
  serviceId: string
): Promise<ServiceCampaign> => {
  try {
    const res = await _getServiceCampaign({ serviceId });
    return res.data;
  } catch (err) {
    console.log(err);
    return Promise.reject(err);
  }
};

export const getCampaignsLatestPublishedAt = async (): Promise<string> => {
  const res = await _getCampaignsLatestPublishedAt();
  return res.data;
};

export type SurveyAnsewerOptions = {
  sessionId: string;
  surveyId: string;
  questionId: string;
  choiceType: string;
  freeText: string;
  choiceIds: string[];
  isPro: boolean;
  isLoginUser: boolean;
  isRoomOwner: boolean;
  diceBot: string | null;
};

export const sendSurveyAnswerInfo = async (
  option: SurveyAnsewerOptions
): Promise<void> => {
  try {
    await _sendSurveyAnswerInfo(option);
  } catch (err) {
    console.log(err);
  }
};

export const checkedSurvey = async (surveyId: string): Promise<void> => {
  try {
    await _checkedSurvey({ surveyId });
  } catch (err) {
    console.log(err);
  }
};

export const getInformations = async (
  surveyId: string | null
): Promise<AdContent> => {
  const res = await _getInformations({ surveyId });
  return res.data;
};
